import { StrictMode, Suspense, lazy, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster, toast } from 'sonner';
import './styles.css';
import { ApiError } from './lib/api';
import { AuthProvider, useAuth } from './lib/auth';
import { Shell } from './layout/Shell';
import { Login } from './pages/Login';
import { Skeleton } from './ui/core';
import { ChangePassword } from './pages/settings/ChangePassword';

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2, refetchOnWindowFocus: true } },
  queryCache: new QueryCache({ onError: (e, q) => { if (q.state.data !== undefined) toast.error((e as Error).message); } }),
  mutationCache: new MutationCache({ onError: (e) => { if (!(e instanceof ApiError && e.fields)) toast.error((e as Error).message); } }),
});

const page = (load: () => Promise<Record<string, unknown>>, name: string) => {
  const C = lazy(async () => ({ default: (await load())[name] as React.ComponentType }));
  return <Suspense fallback={<div className="space-y-3"><Skeleton className="h-8 w-48" /><Skeleton className="h-64 w-full" /></div>}><C /></Suspense>;
};

const router = createBrowserRouter([{
  path: '/',
  element: <Shell />,
  children: [
    { index: true, element: page(() => import('./pages/Dashboard'), 'Dashboard') },
    { path: 'orders', element: page(() => import('./pages/orders/Orders'), 'Orders') },
    { path: 'orders/:id', element: page(() => import('./pages/orders/OrderDetail'), 'OrderDetail') },
    { path: 'returns', element: page(() => import('./pages/orders/Returns'), 'Returns') },
    { path: 'products', element: page(() => import('./pages/catalog/Products'), 'Products') },
    { path: 'products/new', element: page(() => import('./pages/catalog/ProductEditor'), 'ProductEditor') },
    { path: 'products/:id', element: page(() => import('./pages/catalog/ProductEditor'), 'ProductEditor') },
    { path: 'catalog/:tab', element: page(() => import('./pages/catalog/Taxonomy'), 'Taxonomy') },
    { path: 'inventory', element: page(() => import('./pages/Inventory'), 'Inventory') },
    { path: 'customers', element: page(() => import('./pages/customers/Customers'), 'Customers') },
    { path: 'customers/:id', element: page(() => import('./pages/customers/CustomerDetail'), 'CustomerDetail') },
    { path: 'promotions', element: page(() => import('./pages/promotions/Promotions'), 'Promotions') },
    { path: 'promotions/new', element: page(() => import('./pages/promotions/PromotionEditor'), 'PromotionEditor') },
    { path: 'promotions/:id', element: page(() => import('./pages/promotions/PromotionEditor'), 'PromotionEditor') },
    { path: 'notifications', element: page(() => import('./pages/notifications/Notifications'), 'Notifications') },
    { path: 'content', element: page(() => import('./pages/content/Content'), 'Content') },
    { path: 'shipping', element: page(() => import('./pages/Shipping'), 'Shipping') },
    { path: 'support', element: page(() => import('./pages/Support'), 'Support') },
    { path: 'reports', element: page(() => import('./pages/Reports'), 'Reports') },
    { path: 'settings', element: page(() => import('./pages/settings/Settings'), 'Settings') },
    { path: 'settings/:tab', element: page(() => import('./pages/settings/Settings'), 'Settings') },
    { path: '*', element: page(() => import('./pages/NotFound'), 'NotFound') },
  ],
}]);

function Gate({ children }: { children: ReactNode }) {
  const { status, me } = useAuth();
  if (status === 'loading') return <div className="grid min-h-dvh place-items-center"><Skeleton className="h-10 w-10" /></div>;
  if (status === 'signed-out') return <Login mode="login" />;
  if (status === 'setup') return <Login mode="setup" />;
  if (me?.mustChangePassword) return <ChangePassword forced />;
  return <>{children}</>;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <AuthProvider>
        <Gate><RouterProvider router={router} /></Gate>
        <Toaster dir="rtl" position="bottom-left" toastOptions={{ style: { fontFamily: 'IRANSans, system-ui', borderRadius: 2 } }} />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
